import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { match } from 'ts-pattern';

import ImportCsv from '@/features/import-csv';
import type { ExportApiUrl } from '@/features/import-csv/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ContractType } from '@/routes/_staff/contract/_list/-constants';

import useContractTypeParam from './-hooks/useContractTypeParam';
import useLeaseTableRows from './-lease/useLeaseTableRows';
import usePurchaseTableRows from './-purchase/usePurchaseTableRows';
import { useSubscriptionTableRowsByPhase } from './-subscription/useSubscriptionTableRowsByPhase';
import type { ContractImportCsvSearchParams } from './-zod-schema';
import { ContractImportCsvSearchSchema } from './-zod-schema';

const ContractImportCsv = () => {
  const search = Route.useSearch() as ContractImportCsvSearchParams;
  const navigate = Route.useNavigate();
  const { contractType } = useContractTypeParam();

  const subscriptionPhase = search.phase ?? 'contract';
  const subscriptionUseTableRows =
    useSubscriptionTableRowsByPhase(subscriptionPhase);

  const useTableRows = match(contractType)
    .with(ContractType.LEASE, () => useLeaseTableRows)
    .with(ContractType.SUBSCRIPTION, () => subscriptionUseTableRows)
    .with(ContractType.PURCHASE, () => usePurchaseTableRows)
    .otherwise(() => {
      throw new Error('Invalid contract type');
    });

  useEffect(() => {
    if (
      contractType === ContractType.SUBSCRIPTION &&
      search.phase === undefined
    ) {
      navigate({
        to: '/contract/import-csv/$contractType',
        params: { contractType },
        search: { ...search, phase: 'contract' },
      });
    }
  }, [contractType, search.phase, navigate, search]);

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.importCsv.contractType',
  });

  const { t: tCompleteDialog } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.importCsv.completeDialog',
  });

  const { t: tSubscriptionPhases } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.importCsv.subscriptionPhases',
  });

  const title = match(contractType)
    .with(ContractType.LEASE, () => t('title.leaseAsset'))
    .with(
      ContractType.SUBSCRIPTION,
      () =>
        `${t('title.subscription')} - ${tSubscriptionPhases(subscriptionPhase)}`,
    )
    .with(ContractType.PURCHASE, () => t('title.purchase'))
    .otherwise(() => {
      throw new Error('Invalid contract type');
    });

  const exportApiUrl = ((): ExportApiUrl => {
    if (contractType === ContractType.SUBSCRIPTION) {
      return match(subscriptionPhase)
        .with('contract', () => '/v2/contract-csv/subscription-contract')
        .with('tenant', () => '/v2/contract-csv/subscription-contract-tenant')
        .with('plan', () => '/v2/contract-csv/subscription-contract-plan')
        .exhaustive() as ExportApiUrl;
    }
    return match(contractType)
      .with(ContractType.LEASE, () => '/v2/contract-csv/lease-contract')
      .with(ContractType.PURCHASE, () => '/v2/contract-csv/purchase-contract')
      .otherwise(() => {
        throw new Error('Invalid contract type');
      }) as ExportApiUrl;
  })();

  const exportFilename = (() => {
    if (contractType === ContractType.SUBSCRIPTION) {
      return match(subscriptionPhase)
        .with('contract', () => t('filename.subscriptionContract'))
        .with('tenant', () => t('filename.subscriptionTenant'))
        .with('plan', () => t('filename.subscriptionPlan'))
        .exhaustive();
    }
    return match(contractType)
      .with(ContractType.LEASE, () => t('filename.leaseAsset'))
      .with(ContractType.PURCHASE, () => t('filename.purchase'))
      .otherwise(() => {
        throw new Error('Invalid contract type');
      });
  })();

  const templateTitle = (() => {
    if (contractType === ContractType.SUBSCRIPTION) {
      return match(subscriptionPhase)
        .with('contract', () =>
          t('prepareCsvFile.templateTitle.subscriptionContract'),
        )
        .with('tenant', () =>
          t('prepareCsvFile.templateTitle.subscriptionTenant'),
        )
        .with('plan', () => t('prepareCsvFile.templateTitle.subscriptionPlan'))
        .exhaustive();
    }
    return match(contractType)
      .with(ContractType.LEASE, () =>
        t('prepareCsvFile.templateTitle.leaseAsset'),
      )
      .with(ContractType.PURCHASE, () =>
        t('prepareCsvFile.templateTitle.purchase'),
      )
      .otherwise(() => {
        throw new Error('Invalid contract type');
      });
  })();

  const unit = match(contractType)
    .with(ContractType.LEASE, () => t('unit.leaseAssets'))
    .with(ContractType.SUBSCRIPTION, () => t('unit.subscriptionContracts'))
    .with(ContractType.PURCHASE, () => t('unit.purchaseContracts'))
    .otherwise(() => {
      throw new Error('Invalid contract type');
    });

  const updateExisting = (() => {
    if (contractType === ContractType.SUBSCRIPTION) {
      return match(subscriptionPhase)
        .with('contract', () =>
          t('prepareCsvFile.updateExisting.subscriptionContract'),
        )
        .with('tenant', () =>
          t('prepareCsvFile.updateExisting.subscriptionTenant'),
        )
        .with('plan', () => t('prepareCsvFile.updateExisting.subscriptionPlan'))
        .exhaustive();
    }
    return match(contractType)
      .with(ContractType.LEASE, () =>
        t('prepareCsvFile.updateExisting.leaseAsset'),
      )
      .with(ContractType.PURCHASE, () =>
        t('prepareCsvFile.updateExisting.purchase'),
      )
      .otherwise(() => {
        throw new Error('Invalid contract type');
      });
  })();

  const baseLink = `/contract/import-csv/${contractType}`;

  const templateMessage = {
    templateTitle,
    updateExisting,
    completeLinkButton: tCompleteDialog('button'),
  };

  return (
    <ImportCsv
      baseLink={baseLink}
      baseTitle={title}
      exportApiUrl={exportApiUrl}
      exportFilename={exportFilename}
      templateMessage={templateMessage}
      completeLinkUrl="/contract"
      unit={unit}
      search={search}
      navigate={navigate}
      useTableRows={useTableRows}
    />
  );
};

export const Route = createFileRoute(
  '/_staff/contract/import-csv/$contractType/',
)({
  validateSearch: zodValidator(ContractImportCsvSearchSchema),
  component: ContractImportCsv,
});
